import { useState, useEffect, useCallback, memo } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Route, Plus, Edit, Trash2, MapPin, Clock, DollarSign, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Memoized form component to prevent re-renders
const RouteFormFields = memo(({ formData, onChange, stations }) => {
  const handleChange = useCallback((field, value) => {
    onChange(field, value);
  }, [onChange]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Route Name *</Label>
          <Input
            defaultValue={formData.name}
            onBlur={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Harare - Durban via Masvingo"
            required
          />
        </div>
        <div>
          <Label>Route Code *</Label>
          <Input
            defaultValue={formData.code}
            onBlur={(e) => handleChange('code', e.target.value)}
            placeholder="e.g., HRE-DUR-MSV"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Origin *</Label>
          <Input
            value={formData.origin_name || ''}
            onChange={(e) => handleChange('origin_name', e.target.value)}
            placeholder="City or station name"
            required
          />
          <p className="text-xs text-slate-500 mt-1">Enter city name (doesn't need to be a station)</p>
        </div>
        <div>
          <Label>Destination *</Label>
          <Input
            value={formData.destination_name || ''}
            onChange={(e) => handleChange('destination_name', e.target.value)}
            placeholder="City or station name"
            required
          />
          <p className="text-xs text-slate-500 mt-1">Enter city name (doesn't need to be a station)</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Origin Station (optional)</Label>
          <Select 
            value={formData.origin_station_id || undefined}
            onValueChange={(v) => handleChange('origin_station_id', v)}
          >
            <SelectTrigger><SelectValue placeholder="Link to station" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No station</SelectItem>
              {stations.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Destination Station (optional)</Label>
          <Select 
            value={formData.destination_station_id || undefined}
            onValueChange={(v) => handleChange('destination_station_id', v)}
          >
            <SelectTrigger><SelectValue placeholder="Link to station" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No station</SelectItem>
              {stations.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Via (Intermediate stops)</Label>
        <Input
          defaultValue={formData.via}
          onBlur={(e) => handleChange('via', e.target.value)}
          placeholder="e.g., Masvingo, Beitbridge"
        />
      </div>

      <div>
        <Label>Pickup / Boarding Locations (Pick and Drop Points)</Label>
        <textarea
          className="w-full border rounded-md p-2 text-sm min-h-[70px] resize-y"
          defaultValue={Array.isArray(formData.pickup_points) ? formData.pickup_points.join(', ') : (formData.pickup_points || '')}
          onBlur={(e) => handleChange('pickup_points', e.target.value)}
          placeholder="e.g., Harare Roadport, Highglen, Chitungwiza, Norton, Chegutu, Kadoma, Kwekwe, Gweru, Bulawayo, Beitbridge Engen garage, Pietermaritzburg, Durban"
        />
        <p className="text-xs text-slate-500 mt-1">Separate locations with commas. Passengers booking online can select any of these specific pick and drop locations along this route.</p>
      </div>

      <div>
        <Label>Parcel Drop and Pick Points</Label>
        <textarea
          className="w-full border rounded-md p-2 text-sm min-h-[70px] resize-y"
          defaultValue={Array.isArray(formData.parcel_points) ? formData.parcel_points.join(', ') : (formData.parcel_points || '')}
          onBlur={(e) => handleChange('parcel_points', e.target.value)}
          placeholder="e.g., Beitbridge Engen garage, Makado, West Nicholson, Gwanda, Bulawayo, Gweru, Roadport"
        />
        <p className="text-xs text-slate-500 mt-1">Separate locations with commas. Used for luggage manifest and parcel dispatch tracking.</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Distance (km) *</Label>
          <Input
            type="number"
            defaultValue={formData.distance_km}
            onBlur={(e) => handleChange('distance_km', e.target.value)}
            required
          />
        </div>
        <div>
          <Label>Base Price (USD) *</Label>
          <Input
            type="number"
            step="0.01"
            defaultValue={formData.base_price_usd}
            onBlur={(e) => handleChange('base_price_usd', e.target.value)}
            required
          />
        </div>
        <div>
          <Label>Duration (hours) *</Label>
          <Input
            type="number"
            defaultValue={formData.duration_hours}
            onBlur={(e) => handleChange('duration_hours', e.target.value)}
            required
          />
        </div>
      </div>
    </div>
  );
});

RouteFormFields.displayName = 'RouteFormFields';

const AdminRoutes = () => {
  const { token } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    origin_name: '',
    destination_name: '',
    origin_station_id: '',
    destination_station_id: '',
    via: '',
    pickup_points: '',
    parcel_points: '',
    distance_km: '',
    base_price_usd: '',
    duration_hours: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [routesRes, stationsRes] = await Promise.all([
        axios.get(`${API_URL}/routes`),
        axios.get(`${API_URL}/stations`)
      ]);
      setRoutes(routesRes.data);
      setStations(stationsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const parsedPickupPoints = typeof formData.pickup_points === 'string'
        ? formData.pickup_points.split(',').map(s => s.trim()).filter(Boolean)
        : (formData.pickup_points || []);
      const parsedParcelPoints = typeof formData.parcel_points === 'string'
        ? formData.parcel_points.split(',').map(s => s.trim()).filter(Boolean)
        : (formData.parcel_points || []);

      await axios.post(`${API_URL}/routes`, {
        ...formData,
        pickup_points: parsedPickupPoints,
        parcel_points: parsedParcelPoints,
        origin_station_id: formData.origin_station_id === 'none' ? null : formData.origin_station_id,
        destination_station_id: formData.destination_station_id === 'none' ? null : formData.destination_station_id,
        distance_km: parseInt(formData.distance_km),
        base_price_usd: parseFloat(formData.base_price_usd),
        duration_hours: parseInt(formData.duration_hours),
        stops: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route created');
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create route');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const parsedPickupPoints = typeof formData.pickup_points === 'string'
        ? formData.pickup_points.split(',').map(s => s.trim()).filter(Boolean)
        : (formData.pickup_points || []);
      const parsedParcelPoints = typeof formData.parcel_points === 'string'
        ? formData.parcel_points.split(',').map(s => s.trim()).filter(Boolean)
        : (formData.parcel_points || []);

      await axios.put(`${API_URL}/routes/${editingRoute.id}`, {
        ...formData,
        pickup_points: parsedPickupPoints,
        parcel_points: parsedParcelPoints,
        origin_station_id: formData.origin_station_id === 'none' ? null : formData.origin_station_id,
        destination_station_id: formData.destination_station_id === 'none' ? null : formData.destination_station_id,
        distance_km: parseInt(formData.distance_km),
        base_price_usd: parseFloat(formData.base_price_usd),
        duration_hours: parseInt(formData.duration_hours),
        stops: editingRoute.stops || []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route updated');
      setShowEdit(false);
      setEditingRoute(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update route');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm('Are you sure you want to delete this route?')) return;
    try {
      await axios.delete(`${API_URL}/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete route');
    }
  };

  const startEdit = (route) => {
    setEditingRoute(route);
    setFormData({
      name: route.name,
      code: route.code,
      origin_name: route.origin_name || route.origin || '',
      destination_name: route.destination_name || route.destination || '',
      origin_station_id: route.origin_station_id || 'none',
      destination_station_id: route.destination_station_id || 'none',
      via: route.via || '',
      pickup_points: Array.isArray(route.pickup_points) ? route.pickup_points.join(', ') : (route.pickup_points || ''),
      parcel_points: Array.isArray(route.parcel_points) ? route.parcel_points.join(', ') : (route.parcel_points || ''),
      distance_km: String(route.distance_km),
      base_price_usd: String(route.base_price_usd),
      duration_hours: String(route.duration_hours)
    });
    setShowEdit(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      origin_name: '',
      destination_name: '',
      origin_station_id: '',
      destination_station_id: '',
      via: '',
      pickup_points: '',
      parcel_points: '',
      distance_km: '',
      base_price_usd: '',
      duration_hours: ''
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-routes-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Manage Routes
            </h1>
            <p className="text-slate-600">Configure bus routes and pricing</p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="btn-panashe" onClick={resetForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Route
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add New Route</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  <RouteFormFields 
                    formData={formData} 
                    onChange={handleFieldChange} 
                    stations={stations}
                  />
                  <div className="flex justify-end gap-2 mt-6">
                    <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="btn-panashe" disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create Route
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Routes Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </CardContent>
              </Card>
            ))
          ) : routes.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="p-8 text-center text-slate-500">
                <Route className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No routes configured yet</p>
              </CardContent>
            </Card>
          ) : (
            routes.map(route => (
              <Card key={route.id} className="card-panashe hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-heading font-bold text-lg">{route.name}</h3>
                      <Badge variant="outline" className="font-mono">{route.code}</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(route)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(route.id)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span>{route.origin_name || route.origin || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-red-600" />
                      <span>{route.destination_name || route.destination || 'N/A'}</span>
                    </div>
                    {route.via && (
                      <div className="text-slate-500 text-xs">via {route.via}</div>
                    )}
                    {route.pickup_points?.length > 0 && (
                      <div className="text-xs text-blue-600 font-medium">
                        🚏 {route.pickup_points.length} pickup/drop points
                      </div>
                    )}
                    {route.parcel_points?.length > 0 && (
                      <div className="text-xs text-purple-600 font-medium">
                        📦 {route.parcel_points.length} parcel stations
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t text-sm">
                    <div className="flex items-center gap-1 text-slate-600">
                      <Clock className="h-4 w-4" />
                      {route.duration_hours}h
                    </div>
                    <div className="flex items-center gap-1 text-slate-600">
                      {route.distance_km} km
                    </div>
                    <div className="flex items-center gap-1 font-bold text-panashe-blue">
                      <DollarSign className="h-4 w-4" />
                      {route.base_price_usd}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Route</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit}>
              <RouteFormFields 
                formData={formData} 
                onChange={handleFieldChange} 
                stations={stations}
              />
              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-panashe" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Update Route
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminRoutes;
